import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { UtilService } from '../core/services/util.service';
import { CybersourceThreeDSStatus } from '../core/enums/cybersourceThreeDSStatus.enum';
import { OrderCreatedResponse } from '../features/events/services/checkout-session.service';
import Order, { OrderWithPixelId } from '../core/models/order';

type DeviceDataParams = {
  accessToken: string;
  referenceId: string;
  deviceDataCollectionUrl: string;
};

type DeviceDataResponse = {
  MessageType: string;
  SessionId: string;
  Status: boolean;
  payment_history_id: number;
};

type WaitValidationResultResponse =
  | OrderCreatedResponse
  | {
      status: CybersourceThreeDSStatus;
      errorInformation: { reason: CybersourceThreeDSStatus; message: string };
      clientReferenceInformation: { code: string };
      processorInformation: { responseCode: string };
    };

type HandleThreeSessionParam = {
  ddp: DeviceDataParams;
  paymentHistoryId: number;
  onGettingResult(): void;
};

type HandleThreeSessionResponse = {
  order: Order;
  pixelId?: number;
  gtmId?: string;
};

type ValidatePayerAuthenticationResponse =
  | CheckPayerAuthenticationCybersourceResponse
  | OrderCreatedResponse;

type CheckPayerAuthenticationCybersourceResponse = {
  id: string;
  submitTimeUtc: string;
  status: CybersourceThreeDSStatus;
  errorInformation: { reason: CybersourceThreeDSStatus; message: string };
  consumerAuthenticationInformation: ConsumerAuthenticationInformation;
};

export type ErrorInformation = {
  reason: string;
  message: string;
  code: string;
};

type ConsumerAuthenticationInformation = {
  accessToken: string;
  acsTransactionId: string;
  acsUrl: string;
  authenticationTransactionId: string;
  challengeRequired: string;
  pareq: string;
  specificationVersion: string;
  stepUpUrl: string;
  threeDSServerTransactionId: string;
  veresEnrolled: string;
  directoryServerTransactionId: string;
  acsOperatorID: string;
  acsReferenceNumber: string;
};

const TEN_SECONDS = 10 * 1000;
const CARDINAL_COLLECTION_FORM_ID = '#cardinal_collection_form';
const CARDINAL_COLLECTION_INPUT_ID = '#cardinal_collection_form_input';
const STEP_UP_FORM_ID = '#step-up-form';
const STEP_UP_FORM_INPUT_ID = '#step-up-form-input';
const STEP_UP_FORM_INPUT_REFERENCE_ID = '#step-up-form-input-reference-id';
const STEP_UP_IFRAME_CONTAINER_ID = '#step-up-iframe-position';
const PURCHASE_AUTHENTICATION_BTN_ID = '#purchaseAuthenticationBtn';

@Injectable({
  providedIn: 'root',
})
export class ThreeDSCybersourceService {
  constructor(
    private readonly http: HttpClient,
    private readonly utilService: UtilService
  ) {}

  async handleThreeDSSession({
    ddp,
    paymentHistoryId,
    onGettingResult,
  }: HandleThreeSessionParam): Promise<
    HandleThreeSessionResponse | ErrorInformation | null
  > {
    const deviceData = await this.collectDeviceData(ddp);

    if (!deviceData?.Status) {
      return null;
    }

    const postData = this.buildOrderData(ddp.referenceId, paymentHistoryId);

    let result: (Order & { pixelId: number; gtmId?: string }) | null = null;

    while (!result) {
      const validationResult = await this.checkAndValidateAndResultValidation(
        postData,
        ddp,
        onGettingResult
      );

      if (!validationResult) {
        return null;
      }

      if ('data' in validationResult) {
        return {
          pixelId: validationResult.data.pixel_id,
          gtmId: validationResult.data.gtm_id,
          order: {
            buyerEmail: validationResult.data.order.buyer_email,
            buyerName: validationResult.data.order.buyer_full_name,
            totalToPay: validationResult.data.order.total,
            subTotal: validationResult.data.order.sub_total,
            serviceFee: validationResult.data.order.service_fee,
            insuranceFee: validationResult.data.order.insurance_fee,
            slug: validationResult.data.order.slug,
          } as Order,
        };
      }

      const isOrder = 'slug' in validationResult;

      if (isOrder) {
        result = validationResult as Order & {
          pixelId: number;
          gtmId?: string;
        };
      }

      if (
        !isOrder &&
        'errorInformation' in validationResult &&
        'processorInformation' in validationResult
      ) {
        return {
          code: validationResult.processorInformation.responseCode,
          reason: validationResult.errorInformation.reason,
          message: validationResult.errorInformation.message,
        };
      }
      if (
        !isOrder &&
        'status' in validationResult &&
        validationResult.status !== CybersourceThreeDSStatus.PENDING
      ) {
        return null;
      }

      this.removeStepUpIframe();
    }

    const order = result;
    return { order, pixelId: result.pixelId, gtmId: result.gtmId };
  }

  async checkAndValidateAndResultValidation(
    postData: any,
    ddp: DeviceDataParams,
    onGettingResult: () => void
  ) {
    const vpa = await this.validatePayerAuthentication(postData);
    if ('data' in vpa) {
      const {
        id,
        buyer_email,
        created_at,
        buyer_full_name,
        total,
        sub_total,
        service_fee,
        insurance_fee,
        slug,
      } = vpa.data.order;
      return {
        pixelId: vpa.data.pixel_id,
        gtmId: vpa.data.gtm_id,
        buyerName: buyer_full_name,
        buyerEmail: buyer_email,
        totalToPay: total,
        subTotal: sub_total,
        serviceFee: service_fee,
        insuranceFee: insurance_fee,
        id: id,
        createdAt: new Date(created_at),
        slug: slug,
      };
    }

    if (
      vpa.status === CybersourceThreeDSStatus.FAILED ||
      vpa.status === CybersourceThreeDSStatus.INVALID
    ) {
      return null;
    }

    const accessToken = (vpa as any).consumerAuthenticationInformation
      ?.accessToken;
    const stepUpUrl = (vpa as any).consumerAuthenticationInformation?.stepUpUrl;

    if (!accessToken && 'errorInformation' in vpa) {
      return vpa;
    }

    this.renderStepUpIframe(accessToken, stepUpUrl, ddp.referenceId);

    const validationResult = await this.waitValidationResult(
      ddp.referenceId,
      postData,
      onGettingResult
    );

    return validationResult;
  }

  async waitValidationResult(
    referenceId: string,
    postData: any,
    onGettingResult: () => void
  ): Promise<WaitValidationResultResponse> {
    const closeBtn = document.querySelector<HTMLButtonElement>(
      '#purchaseAuthenticationBtnClose'
    );
    return new Promise((resolve, reject) => {
      onGettingResult();

      this.utilService.getSocket().on(referenceId, async (data) => {
        try {
          closeBtn?.click();
          this.removeStepUpIframe();
          const result = await this.validatePayerAuthenticationResult(
            data,
            postData
          );

          resolve(result as any);
        } catch (error) {
          reject(error);
        }
      });
    });
  }

  async validatePayerAuthenticationResult(
    transactionId: string,
    postData: any
  ) {
    postData.transactionId = transactionId;
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };

    const result = await this.getAuthenticationResultValidation(
      options,
      postData
    );
    return result;
  }

  async collectDeviceData({
    accessToken,
    deviceDataCollectionUrl,
  }: DeviceDataParams) {
    const cardinalCollectionForm = document.querySelector<HTMLFormElement>(
      CARDINAL_COLLECTION_FORM_ID
    )!;
    const cardinalCollectionInput = document.querySelector<HTMLInputElement>(
      CARDINAL_COLLECTION_INPUT_ID
    )!;

    cardinalCollectionForm.action = deviceDataCollectionUrl;
    cardinalCollectionInput.value = accessToken;

    const data = await this.getDeviceData(cardinalCollectionForm);
    return data;
  }

  private buildOrderData(referenceId: string, paymentHistoryId: number) {
    const postData: any = {
      referenceId,
      paymentHistoryId,
    };

    return postData;
  }

  private async validatePayerAuthentication(
    postData: any
  ): Promise<ValidatePayerAuthenticationResponse> {
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };

    const vpaData: any = await this.get(
      options,
      postData,
      'checkPayerAuthenticationEnrollment'
    );

    return vpaData;
  }

  private renderStepUpIframe(
    accessToken: string,
    stepUpURL: string,
    referenceId: string
  ) {
    const stepUpform =
      document.querySelector<HTMLFormElement>(STEP_UP_FORM_ID)!;
    const stepUpformInput = document.querySelector<HTMLInputElement>(
      STEP_UP_FORM_INPUT_ID
    )!;
    const stepUpformInputReference = document.querySelector<HTMLInputElement>(
      STEP_UP_FORM_INPUT_REFERENCE_ID
    )!;
    const stepUpIframeContainer = document.querySelector<HTMLInputElement>(
      STEP_UP_IFRAME_CONTAINER_ID
    )!;
    const btn = document.querySelector<HTMLButtonElement>(
      PURCHASE_AUTHENTICATION_BTN_ID
    );

    const iframe = document.createElement('iframe');
    iframe.style.width = '400px';
    iframe.style.height = '750px';

    iframe.name = 'step-up-iframe';
    iframe.id = 'step-up-iframe';

    stepUpIframeContainer.appendChild(iframe);

    stepUpform.action = stepUpURL;
    stepUpformInput.value = accessToken;
    stepUpformInputReference.value = referenceId;

    stepUpform.submit();

    btn?.click();
  }

  private removeStepUpIframe() {
    const iframe = document.querySelector<HTMLInputElement>('#step-up-iframe')!;
    if (iframe) {
      iframe.remove();
    }
  }

  private async getAuthenticationResultValidation(
    options: { headers: HttpHeaders },
    data: any
  ) {
    return this.get(options, data, 'validateAuthenticationResult');
  }

  private getDeviceData(form: HTMLFormElement) {
    return new Promise<DeviceDataResponse | null>((resolve, reject) => {
      form.submit();
      window.addEventListener('message', (event) => {
        if (event.origin === environment.apiCardinal) {
          resolve(JSON.parse(event.data) as DeviceDataResponse);
        }
      });

      setTimeout(() => {
        resolve(null);
      }, TEN_SECONDS);
    });
  }

  private async get(
    options: { headers: HttpHeaders },
    data: any,
    cybersourcePath: string
  ) {
    return new Promise((resolve, reject) => {
      this.http
        .post(
          `${environment.apiUrl}/cybersource/${cybersourcePath}`,
          JSON.stringify(data),
          options
        )
        .subscribe({
          next: (resp) => resolve(resp),
          error: (err) => reject(err),
        });
    });
  }
}
